import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import type { Photo } from "@/types";
import type { PlaybackState, PlaybackTier } from "../types";

/**
 * Tier thresholds (fps)
 */
const NORMAL_TIER_MAX = 60;
const FAST_TIER_MAX = 200;

/**
 * Buffer sizes per tier
 */
const BUFFER_SIZES: Record<PlaybackTier, number> = {
  normal: 100,
  fast: 200,
  ultra: 400,
};

/**
 * Hook return type
 */
export interface UsePlaybackControllerReturn {
  state: PlaybackState;
  logicalIndex: number;
  displayIndex: number;
  jumpToIndex: (index: number, bypassCache?: boolean) => void;
  stepFrame: (direction: 1 | -1) => void;
  isFrameReady: (index: number) => boolean;
}

/**
 * Calculate playback parameters based on velocity
 */
function calculatePlaybackParams(velocity: number): {
  tier: PlaybackTier;
  stride: number;
  bufferSize: number;
} {
  const absVelocity = Math.abs(velocity);

  let tier: PlaybackTier;
  if (absVelocity <= NORMAL_TIER_MAX) {
    tier = "normal";
  } else if (absVelocity <= FAST_TIER_MAX) {
    tier = "fast";
  } else {
    tier = "ultra";
  }

  // Stride calculation: at least 1, increases with velocity
  const stride = Math.max(1, Math.round(absVelocity / 60));

  return {
    tier,
    stride,
    bufferSize: BUFFER_SIZES[tier],
  };
}

/**
 * Hook for controlling playback with 3-tier system
 *
 * Features:
 * - 3-tier system (normal/fast/ultra) based on velocity
 * - Two indices: logical (where we want to be) and display (what we show)
 * - Never shows uncached frames (principle: no white frames)
 * - RequestAnimationFrame-based animation loop
 *
 * @param photos - Array of photos
 * @param velocity - Current playback velocity (fps, negative = backwards)
 * @param isImageCached - Function to check if image URL is cached
 */
export function usePlaybackController(
  photos: Photo[],
  velocity: number,
  isImageCached: (url: string) => boolean
): UsePlaybackControllerReturn {
  const [logicalIndex, setLogicalIndex] = useState(0);
  // Use ref as source of truth for displayIndex to avoid race conditions
  // The state was causing desync with ref at high velocities
  const displayIndexRef = useRef<number>(0);
  // forceRender triggers re-render when displayIndex changes (ref doesn't trigger re-renders)
  const [, forceRender] = useReducer((x) => x + 1, 0);
  const [state, setState] = useState<PlaybackState>(() => {
    const params = calculatePlaybackParams(0);
    return {
      ...params,
      logicalFps: 0,
      effectiveFps: 0,
      lastShownIndex: 0,
      isFrameReady: true,
    };
  });

  // Refs for animation loop
  const animationFrameRef = useRef<number | null>(null);
  const lastFrameTimeRef = useRef<number>(0);
  const accumulatorRef = useRef<number>(0);
  // Note: displayIndexRef is declared above as source of truth
  const fpsCounterRef = useRef<{ frames: number; lastTime: number }>({
    frames: 0,
    lastTime: performance.now(),
  });

  /**
   * Find the closest cached frame to a target index.
   * Only searches in the direction of movement to prevent stalling.
   * If nothing is found ahead, jumps to the target uncached.
   */
  const findClosestCachedFrame = useCallback(
    (targetIndex: number, stride: number, direction: 1 | -1): number => {
      if (photos.length === 0) return 0;

      // Clamp target to valid range
      const clampedTarget = Math.max(
        0,
        Math.min(targetIndex, photos.length - 1)
      );

      // Check target first (prefer thumb400 for playback quality)
      const targetPhoto = photos[clampedTarget];
      const targetUrl = targetPhoto?.urls?.thumb400 || targetPhoto?.urls?.thumb200;
      if (targetUrl && isImageCached(targetUrl)) {
        return clampedTarget;
      }

      // Search only in the direction of movement to avoid going backwards
      // which would cause the player to get stuck in a loop
      const searchRadius = Math.max(stride * 3, 30);

      for (let i = 1; i <= searchRadius; i++) {
        const checkIndex = clampedTarget + i * direction;
        if (checkIndex >= 0 && checkIndex < photos.length) {
          const url = photos[checkIndex]?.urls?.thumb400 || photos[checkIndex]?.urls?.thumb200;
          if (url && isImageCached(url)) {
            return checkIndex;
          }
        }
      }

      // No cached frame found ahead — advance to target anyway
      // to prevent playback from getting stuck
      return clampedTarget;
    },
    [photos, isImageCached]
  );

  /**
   * Advance frame by stride, finding closest cached frame.
   * Clamps at the boundaries (the caller is expected to stop playback
   * once the end is reached).
   */
  const advanceFrame = useCallback(
    (currentIndex: number, stride: number, direction: 1 | -1): number => {
      const targetIndex = currentIndex + stride * direction;

      // Clamp to bounds
      if (targetIndex < 0) return 0;
      if (targetIndex >= photos.length) return photos.length - 1;

      return findClosestCachedFrame(targetIndex, stride, direction);
    },
    [photos.length, findClosestCachedFrame]
  );

  /**
   * Jump to a specific index
   * @param index - Target index to jump to
   * @param bypassCache - If true, jump directly to index even if not cached (for Netflix-style seeking)
   */
  const jumpToIndex = useCallback(
    (index: number, bypassCache?: boolean) => {
      const clampedIndex = Math.max(0, Math.min(index, photos.length - 1));
      setLogicalIndex(clampedIndex);

      if (bypassCache) {
        // Jump directly to the requested index (for Netflix player)
        // The frame may not be cached yet - PlayerImageDisplay will show loading state
        displayIndexRef.current = clampedIndex;
      } else {
        // Find closest cached frame to show immediately (classic behavior)
        const cachedIndex = findClosestCachedFrame(clampedIndex, 1, 1);
        displayIndexRef.current = cachedIndex;
      }
      accumulatorRef.current = 0;
      forceRender(); // Trigger re-render with new displayIndexRef value
    },
    [photos.length, findClosestCachedFrame]
  );

  /**
   * Step one frame in a direction (for keyboard/click navigation)
   */
  const stepFrame = useCallback(
    (direction: 1 | -1) => {
      const newIndex = advanceFrame(displayIndexRef.current, 1, direction);
      displayIndexRef.current = newIndex;
      setLogicalIndex(newIndex);
      forceRender(); // Trigger re-render with new displayIndexRef value
    },
    [advanceFrame]
  );

  /**
   * Check if a frame at index is ready (cached)
   * Uses thumb400 for playback quality, fallback to thumb200
   */
  const isFrameReady = useCallback(
    (index: number): boolean => {
      if (index < 0 || index >= photos.length) return false;
      const photo = photos[index];
      const url = photo?.urls?.thumb400 || photo?.urls?.thumb200;
      if (!url && photo) {
        console.warn(`[usePlaybackController] isFrameReady: Photo at index ${index} missing thumb URL`);
      }
      return url ? isImageCached(url) : false;
    },
    [photos, isImageCached]
  );

  /**
   * Animation loop for playback
   * Throttled to ~60Hz max visual updates while maintaining logical advancement
   */
  const updatePlayback = useCallback(
    (timestamp: number) => {
      const timeSinceLastFrame = timestamp - lastFrameTimeRef.current;

      // Skip if velocity is negligible - but still request next frame
      if (Math.abs(velocity) < 0.1) {
        animationFrameRef.current = requestAnimationFrame(updatePlayback);
        return;
      }

      // Throttle visual updates to ~60Hz (16ms) to reduce DOM thrashing
      // But always accumulate velocity for smooth logical progression
      const MIN_FRAME_TIME = 16;
      if (timeSinceLastFrame < MIN_FRAME_TIME) {
        // Accumulate velocity even when throttled
        accumulatorRef.current += velocity * (timeSinceLastFrame / 1000);
        animationFrameRef.current = requestAnimationFrame(updatePlayback);
        return;
      }

      // Calculate delta time
      const deltaTime = timeSinceLastFrame / 1000;
      lastFrameTimeRef.current = timestamp;

      // Calculate FPS
      fpsCounterRef.current.frames++;
      const fpsElapsed = timestamp - fpsCounterRef.current.lastTime;
      let effectiveFps = state.effectiveFps;
      if (fpsElapsed >= 1000) {
        effectiveFps = fpsCounterRef.current.frames;
        fpsCounterRef.current.frames = 0;
        fpsCounterRef.current.lastTime = timestamp;
      }

      // Accumulate velocity
      accumulatorRef.current += velocity * deltaTime;

      const { tier, stride, bufferSize } = calculatePlaybackParams(velocity);

      // Calculate steps to advance based on accumulated velocity
      const stepsToAdvance = Math.floor(
        Math.abs(accumulatorRef.current) / stride
      );

      if (stepsToAdvance >= 1) {
        const direction = velocity > 0 ? 1 : -1;
        let newIndex = displayIndexRef.current;

        // Advance by calculated steps
        for (let i = 0; i < stepsToAdvance; i++) {
          newIndex = advanceFrame(newIndex, stride, direction);
        }

        // Update state (ref first, then trigger re-render)
        displayIndexRef.current = newIndex;
        setLogicalIndex(newIndex);
        forceRender(); // Trigger re-render with new displayIndexRef value

        // Reset accumulator (keep remainder)
        accumulatorRef.current = accumulatorRef.current % stride;

        // Update playback state
        setState((prev) => ({
          ...prev,
          tier,
          stride,
          bufferSize,
          logicalFps: Math.abs(velocity),
          effectiveFps,
          lastShownIndex: newIndex,
          isFrameReady: isFrameReady(newIndex),
        }));
      }

      // Continue animation loop
      animationFrameRef.current = requestAnimationFrame(updatePlayback);
    },
    [velocity, advanceFrame, isFrameReady, state.effectiveFps]
  );

  /**
   * Start/stop animation loop based on velocity
   */
  useEffect(() => {
    lastFrameTimeRef.current = performance.now();
    animationFrameRef.current = requestAnimationFrame(updatePlayback);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [updatePlayback]);

  /**
   * Update state when velocity changes (for UI feedback)
   */
  useEffect(() => {
    const params = calculatePlaybackParams(velocity);
    setState((prev) => ({
      ...prev,
      ...params,
      logicalFps: Math.abs(velocity),
    }));
  }, [velocity]);

  /**
   * Reset when photos array changes significantly
   */
  useEffect(() => {
    if (photos.length > 0 && displayIndexRef.current >= photos.length) {
      const newIndex = photos.length - 1;
      displayIndexRef.current = newIndex;
      setLogicalIndex(newIndex);
      forceRender(); // Trigger re-render with new displayIndexRef value
    }
  }, [photos.length]);

  return {
    state,
    logicalIndex,
    displayIndex: displayIndexRef.current, // Use ref as source of truth
    jumpToIndex,
    stepFrame,
    isFrameReady,
  };
}
